import React, { useState } from 'react';
import Box from '@mui/material/Box';
import { useNavigate, useMatch, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { useConfirm } from 'material-ui-confirm';
import {
  TextField,
  Checkbox,
  Button,
  LinearProgress,
  Typography,
  Tooltip,
  RadioGroup,
  Radio,
  FormControl,
  FormControlLabel,
  Grid,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Save,
  Info,
  WhatshotRounded as DataRequestIcon,
} from '@mui/icons-material';
import { Page, Row, Fill } from '../util/Page';
import { AUTH_LEVELS } from '../auth/Authorized';
import {
  createDataRequestAction,
  addAttachmentAction,
  notifyOfDataRequestAction,
  saveDataRequestAction,
  deleteDataRequestAction,
  refetchDataRequestsAction,
} from '../../redux/actions/dataRequest';
import BuildingSelector from '../buildingselector/BuildingSelector';
import DataRequestsLoader from '../loaders/DataRequestsLoader';
import Status, { StatusSelector } from './Status';
import { getVariant } from './PastRequests';

import './data-request.css';

/* eslint-disable jsx-a11y/label-has-associated-control */

const ATTACHMENTS = {
  network_topology: 'Network Topology Document',
  mechanical_schedule: 'Mechanical Schedule',
  control_drawing: 'Control Drawing',
  floor_plan: 'Floor Plan',
};

// available attachment types depend on the building's data sources
// the keys here become the attachment's 'label'
const getAttachments = (getField) => {
  const attachmentNames = { ...ATTACHMENTS };
  if (getField('bacnet')) {
    attachmentNames.bacnet_points_list = 'BACNet Device List';
  }
  if (getField('monitor_modbus')) {
    attachmentNames.modbus_registry_document = 'Modbus Registry Document';
  }
  attachmentNames.other = 'Other';
  return attachmentNames;
};

const EditorWrapper = ({ children }) => (
  <div style={{ display: 'grid' }}>
    <Page>
      <Row>
        <Button component={Link} to="/data-request">
          &lt; Back to Project List
        </Button>
        <Fill />
      </Row>
    </Page>
    {children}
  </div>
);

/**
 * A component with internal state which manages a building data request form
 */
const DataRequestEditor = ({
  existing: existingProject,
  dataRequestsById,
  isSuperAdmin,
  variant,
  loaded,
  create,
  attach,
  sendNotificationEmail,
  save,
  del,
  refetch,
}) => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const match = useMatch('/data-request/edit/:reqId');
  const { reqId: initialRequestId } = match?.params ?? {};
  const [requestId, setRequestId] = useState(initialRequestId);
  const [changes, setChanges] = useState({});
  const [saving, setSaving] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const dataRequest = dataRequestsById[requestId];

  if (!loaded) {
    return (
      <EditorWrapper>
        <Page>
          <Row>
            <div className="loading">
              <LinearProgress color="secondary" className="progress" />
              <DataRequestsLoader variant={variant} />
            </div>
          </Row>
        </Page>
      </EditorWrapper>
    );
  }

  if (succeeded) {
    return (
      <EditorWrapper>
        <Page>
          <Row>
            <Typography variant="h5">SUCCESS!</Typography>
          </Row>
          <Row>
            <Typography>Thank you for your submission.</Typography>
          </Row>
          <Row>
            <Typography>
              Onboard will arrange a 30 minute phone conversation to confirm your materials. You
              will receive an email within 24 hours to arrange this meeting.
            </Typography>
          </Row>
        </Page>
      </EditorWrapper>
    );
  }

  const getField = (key, defaultVal = '') => {
    if (changes[key] != null) return changes[key];
    if (dataRequest && dataRequest[key] != null) return dataRequest[key];
    return defaultVal;
  };
  const setField = (key, value) => setChanges({ ...changes, [key]: value });
  const change = (key) => (e) => setField(key, e.target.value);

  const savedStatus = getField('status', 'saved') === 'saved';
  const editable = (isSuperAdmin || savedStatus) && !saving;

  const existingSiteSelected = getField('building_id') > 0;
  const hasBuilding = existingSiteSelected || (getField('name') && getField('address'));
  const hasDataSource =
    getField('bacnet') || getField('monitor_modbus') || getField('other_data_source');
  const deployedTypeValid = !!getField('deploy_type');
  const networkType = !!getField('network_type');
  const validFields =
    hasDataSource &&
    (!getField('bacnet') || (getField('bacnet') && getField('bacnet_confirmed'))) &&
    deployedTypeValid &&
    dataRequest?.attachments.length > 0;
  const canSubmit = validFields && hasBuilding;
  let submitError = 'Please complete these required fields: ';
  let comma = false;
  if (!hasDataSource) {
    submitError += 'a data source';
    comma = true;
  }
  if (!dataRequest?.attachments.length > 0) {
    if (comma) submitError += ', ';
    submitError += 'attach one or more files';
    comma = true;
  }
  if (!hasBuilding) {
    if (comma) submitError += ', ';
    submitError += 'building name and address';
    comma = true;
  }
  if (!deployedTypeValid) {
    if (comma) submitError += ', ';
    submitError += 'configured device type';
    comma = true;
  }
  if (getField('internet_access') !== 'no internet access') {
    if (!networkType) {
      if (comma) submitError += ', ';
      submitError += 'network type';
      comma = true;
    } else if (getField('network_type') === 'static_ip') {
      if (!getField('ip_addr')) {
        if (comma) submitError += ', ';
        submitError += 'static ip address';
        comma = true;
      }
      if (!getField('subnet')) {
        if (comma) submitError += ', ';
        submitError += 'subnet address';
        comma = true;
      }
      if (!getField('gateway')) {
        if (comma) submitError += ', ';
        submitError += 'gateway ip address';
        comma = true;
      }
    }
  }
  if (!getField('bacnet_confirmed')) {
    if (comma) submitError += ', ';
    submitError += 'bacnet network confirmation';
    comma = true;
  }
  if (!getField('bacnet_onboard_confirmed')) {
    if (comma) submitError += ', ';
    submitError += 'onboard bacnet network confirmation';
    comma = true;
  }
  if (!getField('bacnet_discovery_confirmed')) {
    if (comma) submitError += ', ';
    submitError += 'bacnet devices discoverable confirmation';
    comma = true;
  }
  if (getField('deploy_type') === 'configured device' && !getField('mail_addr')) {
    if (comma) submitError += ', ';
    submitError += 'mailing address for onboard device';
    comma = true;
  }

  // {label: [attachments]}
  const attachmentsByLabel =
    dataRequest?.attachments.reduce((o, a) => {
      const labelAttachments = o[a.label] ?? [];
      labelAttachments.push(a);
      // eslint-disable-next-line no-param-reassign
      o[a.label] = labelAttachments;
      return o;
    }, {}) || {};
  const attachmentNames = getAttachments(getField);
  if (attachmentsByLabel.other?.length > 0) {
    attachmentNames.other = 'Other';
  }

  // if the 'existingProject' flag is explicitly set then use that,
  // otherwise show the set of fields that have information in them
  // otherwise show both sets of fields
  const showBuildingSelector =
    existingProject ||
    existingSiteSelected ||
    (existingProject == null && !getField('name') && !getField('address'));
  const showNewSiteFields = !existingProject && !existingSiteSelected;

  // Map our internal state to a DataRequest object
  const buildReq = () => ({
    building_id: getField('building_id', null),
    new_building: existingSiteSelected
      ? null
      : {
          name: getField('name'),
          address: getField('address'),
          purpose: getField('purpose'),
        },
    bacnet: getField('bacnet', false),
    bacnet_confirmed: getField('bacnet_confirmed', false),
    monitor_modbus: getField('monitor_modbus,', false),
    other_data_source: getField('other_data_source'),
    deploy_type: getField('deploy_type'),
    mail_addr: getField('mail_addr'),
    ip_addr: getField('ip_addr'),
    network: getField('network'),
    interval: getField('interval', '1'),
    context: getField('context'),
    tracking_number: getField('tracking_number'),
  });

  const onSave = (status = 'saved') => {
    const req = buildReq();
    req.status = status;
    const handler = requestId ? (r) => save(requestId, r) : create;
    setSaving(true);
    return handler(req)
      .then((res) => {
        setRequestId(res.id);
        return res.id;
      })
      .then((r) => {
        refetch();
        return r;
      })
      .finally(() => setSaving(false));
  };
  const onSubmit = () =>
    onSave('submitted')
      .then((reqId) => sendNotificationEmail(reqId))
      .then(() => setSucceeded(true));
  const onAttachmentChange = (label) => (event) => {
    event.preventDefault();
    const files = [...event.target.files];
    onSave()
      .then((reqId) => Promise.all(files.map((file) => attach(file, reqId, label))))
      .then(refetch);
  };

  const containerSpacing = 6;
  const leftItemXs = 3;
  const rightItemXs = 9;
  const attachCheckXs = 6;
  const attachButtonXs = 6;
  return (
    <EditorWrapper>
      <Page name="new-data-request">
        <Row>
          <Grid container spacing={containerSpacing}>
            <Grid item>
              We are excited to help get your project launched! Please fill in all of the required
              fields and confirm the required connections for your new project. Not filling out all
              required fields may delay the start of your project.
            </Grid>
          </Grid>
        </Row>
        {!savedStatus ? (
          <>
            <Row>
              <Grid container spacing={containerSpacing}>
                <Grid item xs={leftItemXs}>
                  <label>Request Status</label>
                </Grid>
                <Grid item xs={rightItemXs}>
                  {isSuperAdmin ? (
                    <StatusSelector
                      status={getField('status')}
                      onValueChange={(status) => onSave(status)}
                    />
                  ) : (
                    <Status text={getField('status')} />
                  )}
                </Grid>
              </Grid>
            </Row>
            <Row>
              <Grid container spacing={containerSpacing}>
                <Grid item xs={leftItemXs}>
                  <label>Tracking Number</label>
                </Grid>
                <Grid item xs={rightItemXs}>
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    placeholder="We'll update this once your device ships"
                    value={getField('tracking_number')}
                    onChange={change('tracking_number')}
                    disabled={!isSuperAdmin}
                  />
                  {isSuperAdmin && getField('tracking_number') === changes.tracking_number ? (
                    <Button
                      onClick={() =>
                        onSave(getField('status')).then(() => setField('tracking_number', null))
                      }
                      variant="contained"
                      className="save-button"
                      endIcon={<Save />}
                    >
                      Save Tracking
                    </Button>
                  ) : null}
                </Grid>
              </Grid>
            </Row>
          </>
        ) : null}
        {showBuildingSelector ? (
          <Row>
            <Grid container spacing={containerSpacing}>
              <Grid item xs={leftItemXs}>
                <label>Select Project</label>
              </Grid>
              <Grid item xs={rightItemXs}>
                <BuildingSelector
                  selectedBuildingId={getField('building_id', -1)}
                  handleSelection={change('building_id')}
                  disabled={!editable}
                />
              </Grid>
            </Grid>
          </Row>
        ) : null}
        {showNewSiteFields ? (
          <>
            <Row>
              <Grid container spacing={containerSpacing}>
                <Grid item xs={leftItemXs}>
                  <label className="required" htmlFor="project-name">
                    Project Name
                  </label>
                </Grid>
                <Grid item xs={rightItemXs}>
                  <TextField
                    id="project-name"
                    required
                    error={!getField('name')}
                    placeholder="Baker Street Lab"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={getField('name')}
                    onChange={change('name')}
                    disabled={!editable}
                  />
                </Grid>
              </Grid>
            </Row>
            <Row>
              <Grid container spacing={containerSpacing}>
                <Grid item xs={leftItemXs}>
                  <label htmlFor="building-purpose">Building Purpose</label>
                </Grid>
                <Grid item xs={rightItemXs}>
                  <TextField
                    id="building-purpose"
                    placeholder="Office, Stadium, Hospital, Batcave"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={getField('purpose')}
                    onChange={change('purpose')}
                    disabled={!editable}
                  />
                </Grid>
              </Grid>
            </Row>
            <Row>
              <Grid container spacing={containerSpacing}>
                <Grid item xs={leftItemXs}>
                  <label className="required" htmlFor="building-address">
                    Address
                  </label>
                </Grid>
                <Grid item xs={rightItemXs}>
                  <TextField
                    id="building-address"
                    required
                    error={!getField('address')}
                    placeholder="221B Baker Street, London, England"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={getField('address')}
                    onChange={change('address')}
                    disabled={!editable}
                  />
                </Grid>
              </Grid>
            </Row>
          </>
        ) : null}
        <Row>
          <Grid container spacing={containerSpacing}>
            <Grid item xs={leftItemXs}>
              <Tooltip
                title={
                  <Typography>
                    Please indicate which data communication protocol is supported on your
                    building’s network?
                  </Typography>
                }
              >
                <label className="required">
                  Data Source <Info fontSize="small" />
                </label>
              </Tooltip>
            </Grid>
            <Grid item xs={rightItemXs}>
              <Checkbox
                checked={getField('bacnet', false)}
                onChange={(e) => setField('bacnet', e.target.checked)}
                disabled={!editable}
              />
              <label>BACnet</label>
              <Checkbox
                checked={getField('monitor_modbus', false)}
                onChange={(e) => setField('monitor_modbus', e.target.checked)}
                disabled={!editable}
              />
              <label>Modbus </label>
              <TextField
                id="other-data-source"
                placeholder="Other source"
                variant="outlined"
                size="small"
                value={getField('other_data_source')}
                onChange={change('other_data_source')}
                disabled={!editable}
              />
            </Grid>
          </Grid>
        </Row>
        {getField('bacnet') && (
          <>
            <Row>
              <Grid container spacing={containerSpacing}>
                <Grid item xs={leftItemXs} />
                <Grid item xs={rightItemXs}>
                  <label>
                    <Checkbox
                      checked={getField('bacnet_confirmed', false)}
                      onChange={(e) => setField('bacnet_confirmed', e.target.checked)}
                      disabled={!editable}
                      required
                    />
                    Confirm that the building’s network supports BACnet / IP protocol.
                  </label>
                </Grid>
              </Grid>
            </Row>
            <Row>
              <Grid container spacing={containerSpacing}>
                <Grid item xs={leftItemXs} />
                <Grid item xs={rightItemXs}>
                  <label>
                    <Checkbox
                      checked={getField('bacnet_onboard_confirmed', false)}
                      onChange={(e) => setField('bacnet_onboard_confirmed', e.target.checked)}
                      disabled={!editable}
                      required
                    />
                    Confirm Onboard software will be installed on the same subnet where the BACnet
                    devices are.
                  </label>
                </Grid>
              </Grid>
            </Row>
            <Row>
              <Grid container spacing={containerSpacing}>
                <Grid item xs={leftItemXs} />
                <Grid item xs={rightItemXs}>
                  <label>
                    <Checkbox
                      checked={getField('bacnet_discovery_confirmed', false)}
                      onChange={(e) => setField('bacnet_discovery_confirmed', e.target.checked)}
                      disabled={!editable}
                      required
                    />
                    Did you verify that the BACnet Devices are discoverable on the network?
                  </label>
                </Grid>
              </Grid>
            </Row>
            <Row>
              <Grid container spacing={containerSpacing}>
                <Grid item xs={leftItemXs}>
                  BACNet Network Details
                </Grid>
                <Grid item xs={rightItemXs} container spacing={containerSpacing}>
                  <Grid item>
                    <TextField
                      id="bacnet-ip-address"
                      placeholder="IP Address"
                      variant="outlined"
                      size="small"
                      value={getField('bacnet_ip_address')}
                      onChange={change('bacnet_ip_address')}
                      disabled={!editable}
                    />
                  </Grid>
                  <Grid item>
                    <TextField
                      id="bacnet-subnet"
                      placeholder="Subnet"
                      variant="outlined"
                      size="small"
                      value={getField('bacnet_subnet')}
                      onChange={change('bacnet_subnet')}
                      disabled={!editable}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Row>
          </>
        )}
        {existingSiteSelected && (
          <Row>
            <Grid container spacing={containerSpacing}>
              <Grid item xs={leftItemXs}>
                <label>Network</label>
              </Grid>
              <Grid item xs={rightItemXs}>
                <FormControl component="fieldset">
                  <RadioGroup value={getField('network')} onChange={change('network')} row>
                    <FormControlLabel
                      value="new"
                      control={<Radio />}
                      label="New Network"
                      disabled={!editable}
                    />
                    <FormControlLabel
                      value="same"
                      control={<Radio />}
                      label="Same Network"
                      disabled={!editable}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Row>
        )}
        <Row>
          <Grid container spacing={containerSpacing}>
            <Grid item xs={leftItemXs}>
              <Tooltip
                title={
                  <Typography>
                    <>
                      <div>Please indicate the requirement to support the deployment on site:</div>
                      <ol type="a">
                        <li>
                          Configured Device: Onboard will ship a physical ‘plug n play’ device to
                          the shipping address provided
                        </li>
                        <li>
                          Virtual Machine: Onboard will deploy its software to an on-site Virtual
                          Machine
                        </li>
                      </ol>
                    </>
                  </Typography>
                }
              >
                <label>
                  Deployment Type <Info fontSize="small" />
                </label>
              </Tooltip>
            </Grid>
            <Grid item xs={rightItemXs}>
              <FormControl component="fieldset">
                <RadioGroup
                  value={getField('deploy_type')}
                  onChange={change('deploy_type')}
                  row
                  required
                >
                  <FormControlLabel
                    control={<Radio />}
                    label="Configured Device"
                    value="configured device"
                    disabled={!editable}
                  />
                  <FormControlLabel
                    control={<Radio />}
                    label="Virtual Machine"
                    value="virtual machine"
                    disabled={!editable}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
        </Row>
        {getField('deploy_type') === 'configured device' ? (
          <>
            <Row>
              <Grid container spacing={containerSpacing}>
                <Grid item xs={leftItemXs}>
                  <label htmlFor="mail-addr">Mailing Address</label>
                </Grid>
                <Grid item xs={rightItemXs}>
                  <TextField
                    id="mail-addr"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={getField('mail_addr')}
                    onChange={change('mail_addr')}
                    disabled={!editable}
                    required
                  />
                </Grid>
              </Grid>
            </Row>
            <Row>
              <Grid container spacing={containerSpacing}>
                <Grid item xs={leftItemXs}>
                  Internet Access
                </Grid>
                <Grid item xs={rightItemXs}>
                  <RadioGroup
                    value={getField('internet_access')}
                    onChange={change('internet_access')}
                    row
                  >
                    <FormControlLabel
                      control={<Radio />}
                      label="Ethernet Port"
                      value="ethernet port"
                      disabled={!editable}
                    />
                    <FormControlLabel
                      control={<Radio />}
                      label="Wifi"
                      value="wifi"
                      disabled={!editable}
                    />
                    <FormControlLabel
                      label="No Internet Access"
                      value="no internet access"
                      control={<Radio />}
                      disabled={!editable}
                    />
                  </RadioGroup>
                </Grid>
              </Grid>
            </Row>
          </>
        ) : null}
        {getField('deploy_type') === 'virtual machine' ? (
          <Row>
            <Grid container spacing={containerSpacing}>
              <Grid item xs={leftItemXs}>
                Virtual Machine OS & Version
              </Grid>
              <Grid item xs={rightItemXs}>
                <TextField
                  id="vm-os-ver"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={getField('vm_os_version')}
                  onChange={change('vm_os_version')}
                  disabled={!editable}
                  required
                />
              </Grid>
            </Grid>
          </Row>
        ) : null}
        {getField('internet_access') !== 'no internet access' ? (
          <Row>
            <Grid container spacing={containerSpacing}>
              <Grid item xs={leftItemXs}>
                <label>Network Details</label>
              </Grid>
              <Grid item xs={rightItemXs}>
                <FormControl component="fieldset">
                  <RadioGroup
                    value={getField('network_type')}
                    onChange={change('network_type')}
                    row
                  >
                    <FormControlLabel
                      control={<Radio />}
                      label="Network uses DHCP"
                      value="dhcp"
                      disabled={!editable}
                    />
                    <Tooltip
                      title={
                        <Typography>
                          Before shipping, we will pre-configure our device with the static IP
                          assigned to it on the building’s local network. Hence we ask for the IP
                          addresses required for configuring the device
                        </Typography>
                      }
                    >
                      <FormControlLabel
                        control={<Radio />}
                        label={
                          <Box>
                            Static IP <Info fontSize="small" />
                          </Box>
                        }
                        value="static_ip"
                        disabled={!editable}
                      />
                    </Tooltip>
                  </RadioGroup>
                  {getField('network_type') === 'static_ip' && (
                    <Grid container spacing={containerSpacing}>
                      <Grid item>
                        <TextField
                          label="IP Address"
                          required
                          disabled={!editable}
                          value={getField('ip_addr')}
                          onChange={change('ip_addr')}
                        />
                      </Grid>
                      <Grid item>
                        <TextField
                          label="Subnet IP"
                          required
                          disabled={!editable}
                          value={getField('subnet')}
                          onChange={change('subnet')}
                        />
                      </Grid>
                      <Grid item>
                        <TextField
                          label="Gateway IP"
                          required
                          disabled={!editable}
                          value={getField('gateway')}
                          onChange={change('gateway')}
                        />
                      </Grid>
                    </Grid>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Row>
        ) : null}
        <Row>
          <Grid container spacing={containerSpacing}>
            <Grid item xs={leftItemXs} />
            <Grid item xs={rightItemXs}>
              <Tooltip
                title={
                  <Typography>
                    <>
                      <div>
                        Onboard&apos;s software needs outbound internet access on the following
                        ports:
                      </div>
                      <ol type="a">
                        <li>TCP port 443 to connect with Onboard&apos;s API and AWS services</li>
                        <li>
                          UDP port 1500 @ 3.87.213.243 and 1501 @ 18.205.92.121 to connect with
                          Onboard&apos;s VPN
                        </li>
                      </ol>
                    </>
                  </Typography>
                }
              >
                <label>
                  <Box>
                    <Checkbox
                      checked={getField('firewall_confirmed', false)}
                      onChange={(e) => setField('firewall_confirmed', e.target.checked)}
                      required
                      disabled={!editable}
                    />
                    Does the building&apos;s network allow Outbound Internet Access over these TCP &
                    UDP ports?
                    <Info fontSize="small" />
                  </Box>
                </label>
              </Tooltip>
            </Grid>
          </Grid>
        </Row>

        <Row>
          <Grid container spacing={containerSpacing}>
            <Grid item xs={leftItemXs}>
              <Tooltip
                title={
                  <Typography>
                    Please indicate the time between readings for all sensors that Onboard will
                    collect
                  </Typography>
                }
              >
                <label>
                  Sampling Rate
                  <Info fontSize="small" />
                </label>
              </Tooltip>
            </Grid>
            <Grid item xs={rightItemXs}>
              <Grid container>
                <Grid item xs={3}>
                  <Checkbox
                    checked={getField('interval') === '1'}
                    onChange={(e) => setField('interval', e.target.checked ? '1' : '')}
                    disabled={!editable}
                  />
                  <label>1 minute</label>
                </Grid>
                <Grid item xs={9}>
                  <TextField
                    placeholder="e.g. 5 minutes"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={getField('interval') !== '1' ? getField('interval') : ''}
                    onChange={change('interval')}
                    disabled={!editable || getField('interval') === '1'}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Row>
        <Row>
          <Grid container spacing={containerSpacing}>
            <Grid item xs={leftItemXs}>
              <label className="required">Context</label>
            </Grid>
            <Grid item xs={rightItemXs}>
              {Object.entries(attachmentNames).map(([label, fieldName]) => (
                <React.Fragment key={label}>
                  <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                  >
                    <Grid item xs={attachCheckXs}>
                      <label>{fieldName}</label>
                    </Grid>
                    {editable ? (
                      <Grid item xs={attachButtonXs}>
                        <Button
                          className="attach-button"
                          variant="outlined"
                          color="secondary"
                          startIcon={<CloudUpload />}
                        >
                          Attach files
                          <input
                            className="upload-input"
                            title=" "
                            type="file"
                            multiple
                            onChange={onAttachmentChange(label)}
                          />
                        </Button>
                      </Grid>
                    ) : null}
                  </Grid>
                  <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                  >
                    {attachmentsByLabel[label]?.map((attachment) => (
                      <Grid item key={attachment.id}>
                        <a href={attachment.url}>{attachment.filename}</a>
                        &nbsp;&nbsp;
                      </Grid>
                    ))}
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          </Grid>
        </Row>
        <Row>
          <Grid container spacing={containerSpacing}>
            <Grid item xs={leftItemXs} />
            <Grid item xs={rightItemXs}>
              <TextField
                id="context"
                placeholder="Add other relevant context for your building here..."
                minRows={3}
                value={getField('context')}
                onChange={change('context')}
                variant="outlined"
                multiline
                fullWidth
                disabled={!editable}
              />
            </Grid>
          </Grid>
        </Row>
      </Page>
      {editable ? (
        <Page>
          <Row>
            <Button
              onClick={() => onSave()}
              variant="contained"
              className="save-button"
              endIcon={<Save />}
            >
              Save
            </Button>
            {requestId ? (
              <>
                <Fill />
                <Button
                  aria-label="delete"
                  variant="contained"
                  onClick={() =>
                    confirm({
                      title: 'Please confirm',
                      description:
                        'This will delete your new project request. All data will be lost!',
                      confirmationText: `Delete ${getField('name')}`,
                    })
                      .then(() => del(requestId))
                      .then(() => navigate('/data-request'))
                      .catch(() => {})
                  }
                  endIcon={<Delete />}
                >
                  Delete
                </Button>
              </>
            ) : null}
            <Fill />
            <Fill />
            <Tooltip title={canSubmit ? '' : submitError}>
              <span>
                <Button
                  disabled={!canSubmit}
                  onClick={onSubmit}
                  variant="contained"
                  className="data-request-submit"
                  endIcon={<DataRequestIcon />}
                >
                  Submit Request
                </Button>
              </span>
            </Tooltip>
          </Row>
        </Page>
      ) : null}
    </EditorWrapper>
  );
};

export default connect(
  ({
    user: { userAuthLevel },
    entities: {
      dataRequests: { byId: dataRequestsById, loaded },
    },
  }) => ({
    loaded,
    dataRequestsById,
    isSuperAdmin: userAuthLevel === AUTH_LEVELS.SUPER_ADMIN,
    variant: getVariant(userAuthLevel),
  }),
  (dispatch) => ({
    create: (req) => dispatch(createDataRequestAction(req)),
    attach: (file, reqId, label) => dispatch(addAttachmentAction(file, reqId, label)),
    sendNotificationEmail: (reqId) => dispatch(notifyOfDataRequestAction(reqId)),
    save: (reqId, req) => dispatch(saveDataRequestAction(reqId, req)),
    del: (reqId) => dispatch(deleteDataRequestAction(reqId)),
    refetch: () => dispatch(refetchDataRequestsAction()),
  })
)(DataRequestEditor);
